import { faGlobe } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { FormControl, InputAdornment, Select } from "@mui/material"
import MenuItem from "@mui/material/MenuItem"
import { styled } from "@mui/material/styles"
import { AppContext } from "components/AppContextProvider"
import theme from "css/theme/light.js"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

const StyledSelect = styled(Select)({
  color: theme.palette.primary["contrastText"],
  fontSize: "14px",
  fontWeight: 400,
  border: "0",
  textTransform: "uppercase"
})

const LanguageButton = ({ showLangName }) => {
  const { i18n } = useTranslation()
  const { availableLanguages, appUser } = useContext(AppContext)
  const [language, setLanguage] = useState()

  useEffect(() => {
    if (!availableLanguages || Object.keys(availableLanguages).length === 0) return

    const browserLang = i18n.language?.split("-")[0]
    let selectedLang = null

    // Priority 1: Browser language
    if (browserLang && availableLanguages[browserLang]) {
      selectedLang = browserLang
    }

    // Priority 2: Customer default language
    else if (appUser?.customer?.defaultLanguage && availableLanguages[appUser.customer.defaultLanguage]) {
      selectedLang = appUser.customer.defaultLanguage
    }

    // Priority 3: Reference language from Locize.app
    else {
      const referenceEntry = Object.entries(availableLanguages).find(
        ([, value]) => value?.isReferenceLanguage === true
      )
      selectedLang = referenceEntry?.[0] || Object.keys(availableLanguages)[0]
    }

    setLanguage(selectedLang)
    i18n.changeLanguage(selectedLang)
  }, [appUser.customer])

  const changeLanguage = (event) => {
    const newLang = event.target.value
    setLanguage(newLang)
    i18n.changeLanguage(newLang)
  }

  return (
    <FormControl>
      {showLangName ? (
        <StyledSelect
          IconComponent={ExpandMoreIcon}
          value={language || ""}
          onChange={changeLanguage}
          autoWidth
          variant="outlined"
          sx={{
            ".MuiSelect-icon": {
              color: theme.palette.primary["contrastText"]
            },
            ".css-1ke0m82-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input": {
              padding: "16px 14px 16px 0"
            },
            ".css-16t0q34-MuiSelect-select-MuiInputBase-input-MuiInput-input:focus": {
              backgroundColor: "transparent"
            },
            ".MuiOutlinedInput-notchedOutline": {
              border: "0"
            }
          }}
          renderValue={(selected) => {
            return selected
          }}
          startAdornment={
            <InputAdornment position="start">
              <FontAwesomeIcon
                color={theme.palette.primary[600]}
                style={{ fontSize: "16px" }}
                icon={faGlobe}
              />
            </InputAdornment>
          }>
          {availableLanguages &&
            Object.keys(availableLanguages).map((lng) => {
              return (
                <MenuItem
                  key={lng}
                  value={lng}
                  sx={{
                    fontWeight: i18n.resolvedLanguage === lng ? "bold" : "disabled",
                    color: i18n.resolvedLanguage !== lng && theme.palette.grey["700"]
                  }}>
                  {availableLanguages[lng].name || lng.toUpperCase()}
                </MenuItem>
              )
            })}
        </StyledSelect>
      ) : (
        <Select
          variant="outlined"
          value={language || ""}
          onChange={changeLanguage}
          IconComponent={() => null}
          renderValue={() => (
            <FontAwesomeIcon
              color={theme.palette.primary["main"]}
              style={{ fontSize: "16px" }}
              icon={faGlobe}
            />
          )}
          sx={{
            padding:"11px 16px 10px 16px",
            ".MuiSelect-icon": {
              color: theme.palette.primary["contrastText"]
            },
            ".MuiSelect-select:focus": {
              backgroundColor: "transparent"
            },
            ".MuiSelect-select": {
              padding: "4px !important"
            },
            ".MuiOutlinedInput-notchedOutline": {
              border: "0"
            }
          }}>
          {availableLanguages &&
            Object.keys(availableLanguages).map((lng) => {
              if (lng in availableLanguages) {
                return (
                  <MenuItem
                    key={lng}
                    value={lng}
                    sx={{
                      fontWeight: i18n.resolvedLanguage === lng ? "bold" : "disabled",
                      color: i18n.resolvedLanguage !== lng && theme.palette.grey.main
                    }}>
                    {availableLanguages[lng].nativeName}
                  </MenuItem>
                )
              }
              return null
            })}
        </Select>
      )}
    </FormControl>
  )
}

export default LanguageButton
